import { MenuItem, MenuNode } from "~/shared/types";

/**
 * Positions items that have a sortOrder at their specified 1-based index.
 * Items without sortOrder keep their original relative order and fill the
 * remaining slots. Items with sortOrder are inserted at that position
 * (1 = first, 2 = second, etc.).
 */
function sortByOrder(nodes: MenuNode[]): MenuNode[] {
  // If no item has sortOrder, return as-is
  if (nodes.every((n) => n.sortOrder === undefined)) return nodes;

  // Separate items with and without sortOrder
  const withOrder = [...nodes]
    .filter((n) => n.sortOrder !== undefined)
    .sort((a, b) => (a.sortOrder as number) - (b.sortOrder as number));
  const withoutOrder = nodes.filter((n) => n.sortOrder === undefined);

  // Start with the unsorted items, then insert the sorted ones at their positions
  const result: MenuNode[] = [...withoutOrder];
  for (const node of withOrder) {
    // sortOrder is 1-based; clamp to valid range
    const pos = Math.min(
      Math.max((node.sortOrder as number) - 1, 0),
      result.length,
    );
    result.splice(pos, 0, node);
  }

  return result;
}

export function buildTree(items: MenuItem[]) {
  const map = new Map<number, MenuNode>();
  const roots: MenuNode[] = [];

  // 1️⃣ Create nodes map
  items.forEach((item) => {
    map.set(item.id, {
      ...item,
      children: [],
    });
  });

  // 2️⃣ Link parent → child
  items.forEach((item) => {
    const node = map.get(item.id);

    if (!node) return;

    if (item.parent?.id) {
      const parentNode = map.get(item.parent.id);

      if (parentNode && parentNode.children) {
        parentNode.children.push(node);
      }
    } else {
      roots.push(node);
    }
  });

  // 3️⃣ Remove empty children arrays and sort each level by sortOrder
  const clean = (node: MenuNode) => {
    if (node.children?.length === 0) {
      delete node.children;
    } else if (node.children) {
      node.children = sortByOrder(node.children);
      node.children.forEach(clean);
    }
  };

  roots.forEach(clean);

  return sortByOrder(roots);
}
